// EetmomentenPage.jsx - Eetmomenten en eetschema
import { motion } from 'framer-motion';
import {
  Clock,
  CheckCircle,
  Utensils,
} from 'lucide-react';
import { PageHeader } from './shared';

const EetmomentenPage = ({ onBack, user, selectedLocation }) => {
  const locationSlug = selectedLocation || user?.selected_location || 'groep-zwaluw';
  
  const eetschema = {
    'groep-zwaluw': {
      naam: 'Groep Zwaluw',
      maaltijden: [
        { naam: 'Ontbijt', tijd: '08:00 - 09:30', icon: '🍳', beschrijving: 'Brood, beleg, thee/koffie' },
        { naam: 'Lunch', tijd: '12:00 - 13:00', icon: '🥪', beschrijving: 'Broodmaaltijd met soep' },
        { naam: 'Avondeten', tijd: '18:00', icon: '🍽️', beschrijving: 'Warme maaltijd', vast: true },
      ],
      afspraken: [
        'Samen aan tafel eten',
        'Geen borden meenemen naar de kamer',
        'Bord en bestek afwassen na het eten',
        'Tafel samen opruimen'
      ]
    },
    'groep-adelaars': {
      naam: 'Groep Adelaars',
      maaltijden: [
        { naam: 'Ontbijt', tijd: '08:00 - 09:30', icon: '🍳', beschrijving: 'Brood, beleg, thee/koffie' },
        { naam: 'Lunch', tijd: '12:00 - 13:00', icon: '🥪', beschrijving: 'Broodmaaltijd met soep' },
        { naam: 'Avondeten', tijd: '18:00', icon: '🍽️', beschrijving: 'Warme maaltijd', vast: true },
      ],
      afspraken: [
        'Samen aan tafel eten',
        'Geen borden meenemen naar de kamer',
        'Bord en bestek afwassen na het eten',
        'Tafel samen opruimen'
      ]
    }
  };

  const schema = eetschema[locationSlug] || eetschema['groep-zwaluw'];

  // Bepaal huidige/volgende maaltijd
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinutes = now.getMinutes();
  const currentTime = currentHour * 60 + currentMinutes;

  const getNextMaaltijd = () => {
    if (currentTime < 8 * 60) return 'Ontbijt';
    if (currentTime < 12 * 60) return 'Lunch';
    if (currentTime < 18 * 60) return 'Avondeten';
    return 'Ontbijt (morgen)';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50/30 to-amber-50/30" data-testid="eetmomenten-page">
      <PageHeader 
        title="Eetmomenten" 
        subtitle={schema.naam}
        onBack={onBack} 
        icon={Utensils} 
        iconGradient="from-orange-500 to-amber-500" 
      />

      <div className="px-4 py-6 pb-32">
        {/* Volgende maaltijd banner */}
        <div className="mb-6 p-4 rounded-2xl bg-gradient-to-br from-orange-400 to-amber-500 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-white/80">Volgende maaltijd</p>
              <h3 className="text-xl font-bold">{getNextMaaltijd()}</h3>
            </div>
          </div>
        </div>

        {/* Maaltijden schema */}
        <div className="mb-6">
          <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">Eetschema</h2>
          <div className="space-y-3">
            {schema.maaltijden.map((maaltijd, idx) => (
              <motion.div
                key={maaltijd.naam}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="p-4 rounded-2xl bg-white border border-slate-200/50 shadow-sm"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center text-2xl">
                    {maaltijd.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">{maaltijd.naam}</h3>
                    <p className="text-sm text-slate-500">{maaltijd.beschrijving}</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-lg font-bold ${maaltijd.vast ? 'text-orange-600' : 'text-slate-700'}`}>
                      {maaltijd.tijd}
                    </span>
                    {maaltijd.vast && (
                      <p className="text-xs text-orange-500 font-medium">Vast tijdstip</p>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Afspraken bij het eten */}
        <div className="mb-6">
          <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">Afspraken bij het eten</h2>
          <div className="p-4 rounded-2xl bg-white border border-slate-200/50 shadow-sm">
            <ul className="space-y-2">
              {schema.afspraken.map((afspraak) => (
                <li key={afspraak} className="flex items-start gap-3 text-sm text-slate-700">
                  <CheckCircle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                  {afspraak}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Motivatie banner */}
        <div className="p-4 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200">
          <h3 className="font-semibold text-emerald-800 mb-2">Samen zorgen voor rust en structuur!</h3>
          <ul className="space-y-1 text-sm text-emerald-700">
            <li>✓ Duidelijk voor iedereen</li>
            <li>✓ Meer rust in de groep</li>
            <li>✓ Fijne en schone woonplek</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EetmomentenPage;
